import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { apiError } from "../api";
import { toast } from "sonner";

const HOME_BY_ROLE = { admin: "/admin", teacher: "/teacher", student: "/" };

export default function Login() {
  const { user, login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (user) navigate(HOME_BY_ROLE[user.role] || "/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!username.trim() || !password) return;
    setBusy(true);
    try {
      const u = await login(username.trim(), password);
      navigate(HOME_BY_ROLE[u.role] || "/", { replace: true });
    } catch (err) {
      toast.error(apiError(err, t));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#08082C] bg-stardust relative flex items-center justify-center p-4">
      <div className="fixed inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-32 start-[10%] w-[28rem] h-[28rem] rounded-full bg-[#7625D8]/20 blur-[110px]" />
        <div className="absolute bottom-[-10%] end-[5%] w-[26rem] h-[26rem] rounded-full bg-[#EF4D9A]/14 blur-[110px]" />
      </div>
      <div className="absolute top-4 end-4 z-10"><LanguageSwitcher /></div>
      <div data-testid="login-card" className="magic-panel relative w-full max-w-md p-8 animate-pop">
        <Sparkles />
        <img src="/assets/levels/castle.jpeg" alt="" className="w-24 h-24 rounded-full object-cover mx-auto ring-4 ring-[#FFC83D]/50 glow-gold animate-floaty" />
        <div className="flex items-center justify-center gap-2 mt-4">
          <Crown className="w-6 h-6 text-[#FFC83D]" fill="currentColor" />
          <h1 className="text-2xl sm:text-3xl font-black text-center bg-gradient-to-r from-[#FFC83D] via-[#FF63B5] to-[#9C42F5] bg-clip-text text-transparent">
            {t("auth.welcome").includes("أهل") ? "رحلة ثالث نحو القمة" : "Journey to the Summit"}
          </h1>
          <Crown className="w-6 h-6 text-[#FFC83D]" fill="currentColor" />
        </div>
        <p className="text-center text-sm text-[#A5A0D6] font-bold mt-1 mb-6">{t("auth.subtitle")}</p>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="login-username" className="input-magic" placeholder={t("auth.username")}
            value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" dir="ltr" />
          <input data-testid="login-password" type="password" className="input-magic" placeholder={t("auth.password")}
            value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" dir="ltr" />
          <button data-testid="login-submit" type="submit" disabled={busy || !username.trim() || !password}
            className="btn-magic w-full text-lg py-3">
            {busy ? t("auth.logging_in") : t("auth.login")}
          </button>
        </form>
      </div>
    </div>
  );
}
